import { Component, Inject, ViewChild } from "@angular/core";
import {
    AbstractNmsWizardStep,
    NmsWizardStepMapData,
    NmsWizardStepModel,
    NmsWizardStepValidationResponse
} from "@nms-angular-toolkit/nms-wizard";
import {
    ANGULARJS_TRANSLATE,
    MONITORING_SERVICE,
    NG_DIALOG
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { EndpointsChangesCallbacks, EndpointsChangesToBeApplied, EndsStepModel } from "./ends-step.model";
import { NmsInputButtonComponent } from "@nms-angular-toolkit/nms-input-button";
import { Device } from "@nms-ng2/app/modules/device/device-backup/device-backup.models";
import { SlotData, PortData, MuxDemuxData } from "./end-step-circuit-dwdm.model";
import { TranslatedText } from "@nms-ng2/app/shared/models/translation.models";
import { CircuitService } from "../../../services/circuit.service";
import { DwdmCircuitWizardValidationUtilsService } from "../../../services/dwdm-circuit-wizard-validation-utils.service";
import { CircuitValidationService } from "../../../services/circuit-validation.service";
import { CircuitDiagram } from "../../../diagram/models/diagram.models";
import { DwdmDiagramWizardConverterService } from "../../diagram/dwdm-diagram-wizard-converter.service";
import { TranspondersStepModel } from "../2-transponders/transponders-step.model";
import { LinksStepModel } from "../4-links/links-step.model";
import { IntermediatesStepModel } from "../3-intermediates/intermediates-step.model";
import { AmplifiersStepModel } from "../5-amplifiers/amplifiers-step.model";
import { OscManagementStepModel } from "../6-osc-management/osc-management-step.model";
import { GeneralStepModel } from "../7-general/general-step.model";
import { MatSelectChange } from "@angular/material/select";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { DwdmCircuitWizardUtilsService } from "../../utils/dwdm-circuit-wizard-utils.service";

/**
 * Componente responsável por renderizar os dados o passo 1 - Ends de circuitos DWDM.
 */
@Component({
    selector: "ends-step",
    templateUrl: "./ends-step.component.html",
    styleUrls: ["./ends-step.component.scss"]
})
export class EndsStepComponent extends AbstractNmsWizardStep {

    /**
     * Cópia do modelo atrelado ao HTML para podermos controlar quando as mudanças devem refletir no formulário.
     * Quando é detectado que uma mudança causa invalidação nos passos posteriores, só podemos refletir a mudança
     * quando o usuário aceitar a invalidação dos próximos passos. Devido ao uso do MatSelect e MatCheckbox nos
     * campos de slots, portas e muxes, foi preciso realizar esse controle manual.
     */
    pendingModel = new EndsStepModel();
    model = new EndsStepModel();

    transponderModel: TranspondersStepModel;
    linksModel: LinksStepModel;
    intermediatesModel: IntermediatesStepModel;
    amplifiersModel: AmplifiersStepModel;
    oscsModel: OscManagementStepModel;
    generalModel: GeneralStepModel;

    editDeviceIcon = "edit_square";

    aEndSlotsOptions: SlotData[] = [];
    aEndPortOptions: PortData[] = [];
    aEndMuxOptions: MuxDemuxData[] = [];
    showAEndLabelMuxEmpty: boolean = false;
    showZEndLabelMuxEmpty: boolean = false;

    zEndSlotsOptions: SlotData[] = [];
    zEndPortOptions: PortData[] = [];
    zEndMuxOptions: MuxDemuxData[] = [];
    aEndPortDisabled = true;
    aEndMuxDisabled = true;
    zEndPortDisabled = true;
    zEndMuxDisabled = true;
    selectKeyTranslated: TranslatedText;
    noLC800KeyTranslated: TranslatedText;
    noMuxAssociatedTranslated: TranslatedText;
    compare: Record<string, (propertyA: any, propertyB: any) => boolean> = {};

    circuitDiagram: CircuitDiagram;

    /**
     * Propriedade que mantém a última modificação do usuário armazenada
     * para posterior aplicação na função @see processChanges.
     */
    lastChangeToBeApplied: EndpointsChangesToBeApplied = null;

    /**
     * Ações que devem ser realizadas para cada tipo de modificação que o usuário pode realizar neste step.
     */
    changesCallbacks: EndpointsChangesCallbacks = {
        addDevice: this.addDevice.bind(this),
        changeEndSlot: this.changeEndSlot.bind(this),
        changeEndPort: this.changeEndPort.bind(this),
        changeEndMuxChange: this.changeEndMuxChange.bind(this),
        changeAEndMuxCheck: this.changeAEndMuxCheck.bind(this),
        changeZEndMuxCheck: this.changeZEndMuxCheck.bind(this)
    };

    @ViewChild("aEndDevice") aEndDevice!: NmsInputButtonComponent;
    @ViewChild("zEndDevice") zEndDevice!: NmsInputButtonComponent;

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
            @Inject(NG_DIALOG) private readonly ngDialog: any,
            @Inject(MONITORING_SERVICE) private readonly monitoringService: any,
            private readonly circuitService: CircuitService,
            private readonly circuitValidationService: CircuitValidationService,
            private readonly circuitWizardValidationUtilsService: DwdmCircuitWizardValidationUtilsService,
            private readonly diagramConverterService: DwdmDiagramWizardConverterService,
            private readonly circuitWizardUtilsService: DwdmCircuitWizardUtilsService) {
        super();
        this.model.aEndHasMux = false;
        this.model.zEndHasMux = false;
        this.selectKeyTranslated = this.translate.instant("dwdm.circuit.wizard.ends.step.select");
        this.noLC800KeyTranslated = this.translate.instant("dwdm.circuit.wizard.ends.step.no.lc.800.cards");
        this.noMuxAssociatedTranslated = this.translate.instant("dwdm.circuit.wizard.ends.step.no.mux.associated");
        this.configureSelectFieldTypeCompare();
    }

    onClickAEndDevice = () => {
        this.openDeviceSelector("aEndDevice", this.aEndDevice);
    }

    onClickZEndDevice = () => {
        this.openDeviceSelector("zEndDevice", this.zEndDevice);
    }

    goingToNextStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);

        return this.resolveModel();
    }

    async comingFromNextStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        const model: Promise<NmsWizardStepModel> = this.resolveModel();
        await this.updateDiagramModel(wizardStepsCacheMap);

        return model;
    }

    async comingFromPreviousStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        const endsModel = wizardStepsCacheMap["EndsStepModel"] as EndsStepModel;

        this.transponderModel = wizardStepsCacheMap["TranspondersStepModel"] as TranspondersStepModel;
        this.intermediatesModel = wizardStepsCacheMap["IntermediatesStepModel"] as IntermediatesStepModel;
        this.linksModel = wizardStepsCacheMap["LinksStepModel"] as LinksStepModel;
        this.amplifiersModel = wizardStepsCacheMap["AmplifiersStepModel"] as AmplifiersStepModel;
        this.oscsModel = wizardStepsCacheMap["OscManagementStepModel"] as OscManagementStepModel;
        this.generalModel = wizardStepsCacheMap["GeneralStepModel"] as GeneralStepModel;

        if (endsModel) {
            await this.applyDevicesAfterSelection("aEndDevice", this.aEndDevice, endsModel.aEndDevice);
            await this.applyDevicesAfterSelection("zEndDevice", this.zEndDevice, endsModel.zEndDevice);

            this.model = endsModel;

            this.changeEndSlot(["aEndSlots", "aEndPortOptions", this.model.aEndSlots]);
            this.changeEndSlot(["zEndSlots", "zEndPortOptions", this.model.zEndSlots]);
            this.setPortOptions(this.aEndSlotsOptions, this.model.aEndSlots, "aEndPortOptions");
            this.setPortOptions(this.zEndSlotsOptions, this.model.zEndSlots, "zEndPortOptions");
            this.changeAEndMuxCheck([this.model.aEndHasMux]);
            this.changeZEndMuxCheck([this.model.zEndHasMux]);
        }

        await this.updateDiagramModelWithCurrentValues();
        this.stepLoaded = true;

        return this.resolveModel();
    }

    async allowPrevious(): Promise<NmsWizardStepValidationResponse> {
        return this.validate();
    }

    async allowNext(): Promise<NmsWizardStepValidationResponse> {
        const error = this.circuitWizardValidationUtilsService.getDeviceSlotPortError(this.model);
        if (error) {
            return Promise.reject(new NmsWizardStepValidationResponse(false, this.translate.instant(error)));
        }

        const endsData = this.circuitWizardValidationUtilsService.buildEndsData(this.model);

        return this.circuitValidationService.validateEnds(endsData);
    }

    async allowFinish(): Promise<NmsWizardStepValidationResponse> {
        return this.validate();
    }

    validate(): Promise<NmsWizardStepValidationResponse> {
        return Promise.resolve(new NmsWizardStepValidationResponse(true));
    }

    resolveModel(): Promise<NmsWizardStepModel> {
        return Promise.resolve(this.model);
    }

    openDeviceSelector(endDevice, inputEndDevice: NmsInputButtonComponent): void {
        const currentDevice = this.model[endDevice];
        const applyDevice = async (selected: Device): Promise<void> => {
            this.lastChangeToBeApplied = {
                change: "addDevice",
                values: [endDevice, inputEndDevice, selected]
            }
            setTimeout(() => {
                this.emitChangesWereMade(this.getCurrentStepModel());
            }, 100);
        };

        this.openDevicesModal(applyDevice, currentDevice);
    }

    async applyDevicesAfterSelection(endDevice, inputEndDevice: NmsInputButtonComponent, device: Device) {
        this.resetDataWhenChangeDevice(this.model[endDevice], endDevice);
        this.model[endDevice] = device;
        inputEndDevice.fieldName = device.name;
        inputEndDevice.model = device.name;
        inputEndDevice.iconButtonChanged.emit(this.editDeviceIcon);
        await this.loadDeviceData(device.id, endDevice);
    }

    openDevicesModal(callback: (device: Device) => void, device: Device | undefined): void {
        this.ngDialog
            .openConfirm({
                template:
                    "templates/features/template/template-instance/modals/add-device-modal.html",
                controller: "AddDeviceModalGenericCtrl",
                className: "extra-large-modal",
                closeByNavigation: true,
                resolve: {
                    devicesAlreadyAdded: () => (device ? [device] : []),
                    deviceModelRestrictionTypes: () => { /* empty */ },
                    monitoringSettings: () =>
                        this.monitoringService.getMonitoringSettings(),
                    allowOnlyOneEquipment: () => true,
                    allowOnlyPolledEquipment: () => false
                }
            }).then((devices: Device[]) => callback(devices[0]));
    }

    async loadDeviceData(resourceId: number, endDevice: string) {
        this.loadingConfig.emit({ showLoading: true });
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        try {
            const slots = await this.circuitService.getSlots(resourceId).toPromise();
            const key = endDevice === "aEndDevice" ? "aEndSlotsOptions" : "zEndSlotsOptions";
            this[key] = slots;
        } finally {
            this.loadingConfig.emit({ showLoading: false });
            this.circuitWizardUtilsService.hideDiagram.emit(false);
        }
    }

    async processChanges(cache: NmsWizardStepMapData): Promise<void> {
        if (this.lastChangeToBeApplied) {
            await this.changesCallbacks[this.lastChangeToBeApplied.change](this.lastChangeToBeApplied.values);
            this.lastChangeToBeApplied = null;
        }

        this.updateDiagramModel(cache);
        this.updateStepsModel(cache);
    }

    /**
     * Realiza a atualização dos dados do step após um processamento de mudanças.
     * Quando ocorre invalidação dos steps posteriores, as propriedades que armazenam os
     * dados precisam refletir os novos valores.
     */
    private updateStepsModel(cache: NmsWizardStepMapData): Promise<void> {
        this.model = cache["EndsStepModel"] as EndsStepModel;
        this.transponderModel = cache["TranspondersStepModel"] as TranspondersStepModel;
        this.intermediatesModel = cache["IntermediatesStepModel"] as IntermediatesStepModel;
        this.linksModel = cache["LinksStepModel"] as LinksStepModel;
        this.amplifiersModel = cache["AmplifiersStepModel"] as AmplifiersStepModel;
        this.oscsModel = cache["OscManagementStepModel"] as OscManagementStepModel;
        this.generalModel = cache["GeneralStepModel"] as GeneralStepModel;

        return Promise.resolve();
    }

    private async updateDiagramModel(cache: NmsWizardStepMapData): Promise<void> {
        this.circuitDiagram = this.diagramConverterService.convert(cache);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    private async updateDiagramModelWithCurrentValues(): Promise<void> {
        await this.resolveModel();
        const stepsModel: NmsWizardStepMapData = {
            "EndsStepModel": this.model,
            "TranspondersStepModel": this.transponderModel,
            "IntermediatesStepModel": this.intermediatesModel,
            "LinksStepModel": this.linksModel,
            "AmplifiersStepModel": this.amplifiersModel,
            "OscManagementStepModel": this.oscsModel,
            "GeneralStepModel": this.generalModel
        };
        this.circuitDiagram = this.diagramConverterService.convert(stepsModel);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    /**
     * Retorna os dados atuais.
     * Como a cache @see NmsWizardStepMapData é atualizada apenas no botão próximo do diagrama,
     * é preciso pegar os dados atuais para garantir o funcionamento correto do processChanges.
     */
    private getCurrentStepModel(): NmsWizardStepMapData {
        return {
            "EndsStepModel": this.model,
            "TranspondersStepModel": this.transponderModel,
            "IntermediatesStepModel": this.intermediatesModel,
            "LinksStepModel": this.linksModel,
            "AmplifiersStepModel": this.amplifiersModel,
            "OscManagementStepModel": this.oscsModel,
            "GeneralStepModel": this.generalModel
        } as NmsWizardStepMapData;
    }

    onEndSlotChange(endSlots: string, endPortOptions: string, $event: MatSelectChange) {
        this.pendingModel[endSlots] = $event;
        $event.source.writeValue(this.model[endSlots]);

        this.lastChangeToBeApplied = {
            change: "changeEndSlot",
            values: [endSlots, endPortOptions, $event.value]
        };

        this.emitChangesWereMade(this.getCurrentStepModel());
    }

    onPortsChange(endPorts: string, $event: MatSelectChange) {
        this.pendingModel[endPorts] = $event.value;
        $event.source.writeValue(this.model[endPorts]);

        this.lastChangeToBeApplied = {
            change: "changeEndPort",
            values: [endPorts, $event.value]
        }
        this.emitChangesWereMade(this.getCurrentStepModel());
    }

    onAEndMuxCheckChange($event: MatCheckboxChange) {
        this.pendingModel.aEndHasMux = $event.checked;
        $event.source.checked = this.model.aEndHasMux;

        this.lastChangeToBeApplied = {
            change: "changeAEndMuxCheck",
            values: [$event.checked]
        }

        this.emitChangesWereMade(this.getCurrentStepModel());
    }

    onZEndMuxCheckChange($event: MatCheckboxChange) {
        this.pendingModel.zEndHasMux = $event.checked;
        $event.source.checked = this.model.zEndHasMux;

        this.lastChangeToBeApplied = {
            change: "changeZEndMuxCheck",
            values: [$event.checked]
        }

        this.emitChangesWereMade(this.getCurrentStepModel());
    }

    onEndMuxChange(endMux: string, $event: MatSelectChange) {
        this.pendingModel[endMux] = $event.value;
        $event.source.writeValue(this.model[endMux]);

        this.lastChangeToBeApplied = {
            change: "changeEndMuxChange",
            values: [endMux, $event.value]
        }

        this.emitChangesWereMade(this.getCurrentStepModel());
    }

    async loadMuxes(resourceId: number): Promise<MuxDemuxData[]> {
        try {
            this.loadingConfig.emit( {showLoading: true });
            this.circuitWizardUtilsService.hideDiagram.emit(true);
            return await this.circuitService.getMuxes(resourceId).toPromise();
        } finally {
            this.loadingConfig.emit({ showLoading: false });
        }
    }

    getEndPortsPlaceHolder(endSlots: string) {
        return this.model[endSlots].length > 0 ? this.selectKeyTranslated : "";
    }

    getEndPortsTooltip(endPorts: string): string {
        if (!this.model[endPorts] || this.model[endPorts].length === 0) {
            return "";
        }

        return this.model[endPorts]
            .filter(port => port && port.portName)
            .map(port => port.portName)
            .join(", ");
    }

    getSlotsPlaceholder(endDevice: string, endSlotsOptions: string) {
        if (!this.model[endDevice]) {
            return "";
        }

        return this[endSlotsOptions].length > 0 ? this.selectKeyTranslated : this.noLC800KeyTranslated;
    }

    getTooltipSlots(endDevice: string, endSlot: string, endSlotsOptions: string) {
        if (this.model[endDevice] && this[endSlotsOptions].length === 0) {
            return this.noLC800KeyTranslated;
        }

        if (this.model[endDevice] && this.model[endSlot].length > 0) {
            return this.model[endSlot]
                .filter(slot => slot && slot.slotName)
                .map(slot => slot.slotName)
                .join(", ");
        }

        return "";
    }

    getMuxPlaceholder(endHasMux: string, endMuxOptions: string) {
        if (!this.model[endHasMux]) {
            return "";
        }

        if (this[endMuxOptions].length === 0) {
            return this.noMuxAssociatedTranslated;

        }
        return this[endMuxOptions].length > 0 ? this.selectKeyTranslated : this.aEndMuxOptions[0];
    }

    getMuxTooltip(endHasMux: string, endMux: string, endMuxOptions: string) {
        if (this.model[endHasMux] && this[endMuxOptions].length === 0) {
            return this.noMuxAssociatedTranslated;
        }

        if (this.model[endHasMux] && this.model[endMux]) {
            return this.model[endMux].name;
        }

        return "";
    }

    getTooltipDevice(endDevice: string) {
        if (this.model[endDevice] && this.model[endDevice].name) {
            return this.model[endDevice].name;
        }

        return "";
    }

    /**
     * Esse método é utilizado no compareWith do mat-select, ele é responsável
     * por marcar corretamente os dados do mat-select em uma edição.
     * Neste caso criou-se um método genérico de compare, no qual é passado
     * apenas o nome do campo a ser comparado.
     */
    private configureSelectFieldTypeCompare() {
        ["slotName", "resourceId", "portName"].forEach(fieldName =>
            this.addSelectFieldTypeCompare(fieldName)
        );
    }

    private resetDataWhenChangeDevice(currentDevice: Device, endDevice: string) {
        if (currentDevice) {
            endDevice === "aEndDevice" ? this.clearAEndData() : this.clearZEndData()
        }
    }

    private addSelectFieldTypeCompare(fieldName: string) {
        this.compare[fieldName] = (propertyA: any, propertyB: any) => {
            return propertyA && propertyB && propertyA[fieldName] === propertyB[fieldName];
        };
    }

    private updateAEndMuxValues(muxes: MuxDemuxData[], isChecked: boolean = false) {
        this.aEndMuxOptions = [...muxes].sort((a, b) => a.name.localeCompare(b.name));
        this.showAEndLabelMuxEmpty = this.aEndMuxOptions.length === 0 && isChecked;
        if (!this.model.aEndMux) {
            if (muxes.length === 1) {
                this.model.aEndMux = muxes[0];
                this.emitChangesWereMade(this.getCurrentStepModel());
            } else {
                this.model.aEndMux = null;
            }
        }
    }

    private updateZEndMuxValues(muxes: MuxDemuxData[], isChecked: boolean = false) {
        this.zEndMuxOptions = [...muxes].sort((a, b) => a.name.localeCompare(b.name));
        this.showZEndLabelMuxEmpty = this.zEndMuxOptions.length === 0 && isChecked;
        if (!this.model.zEndMux) {
            if (muxes.length === 1) {
                this.model.zEndMux = muxes[0];
                this.emitChangesWereMade(this.getCurrentStepModel());
            } else {
                this.model.zEndMux = null;
            }
        }
    }

    private setAEndPorts(isDisabled: boolean) {
        this.aEndPortDisabled = isDisabled;
        this.aEndMuxDisabled = isDisabled;
        if (isDisabled) {
            this.model.aEndPorts = [];
            this.model.aEndHasMux = false;
            this.model.aEndMux = null;
            this.aEndMuxOptions = [];
        } else {
            this.model.aEndPorts = this.updatePortsAfterSlotUnchecked(this.model.aEndPorts, this.model.aEndSlots);
        }
    }

    private setZEndPorts(isDisabled: boolean) {
        this.zEndPortDisabled = isDisabled;
        this.zEndMuxDisabled = isDisabled;
        if (isDisabled) {
            this.model.zEndPorts = [];
            this.model.zEndHasMux = false;
            this.model.zEndMux = null;
            this.zEndMuxOptions = [];
        } else {
            this.model.zEndPorts = this.updatePortsAfterSlotUnchecked(this.model.zEndPorts, this.model.zEndSlots);
        }
    }

    /**
     * Remove do modelo as portas de um slot que tenha sido desmarcado, caso ainda exista outro selecionado.
     *
     * @param endPorts - portas selecionadas
     * @param endSlots - slots selecionados
     * @returns - portas que devem ser mantidas
     */
    private updatePortsAfterSlotUnchecked(endPorts: PortData[], endSlots: SlotData[]) {
        return endPorts.reduce((endPort, acc) => {
            const isSlotSelected = endSlots.find(endSlot =>
                acc.chassisNo === endSlot.chassisNo && acc.slotNo === endSlot.slotNo
            );

            if (isSlotSelected) {
                endPort.push(acc);
            }
            return endPort;
        }, []);
    }

    private setPortOptions(slotOptions: SlotData[], slotsSelected: SlotData[], endPortOptions: string) {
        const slots: SlotData[] = slotOptions.filter(item =>
            slotsSelected.some(slotSelected => slotSelected.slotNo === item.slotNo &&
                slotSelected.slotName === item.slotName && slotSelected.slotModel === item.slotModel
            )
        );
        this[endPortOptions] = slots.flatMap(slot => slot.ports ?? []);
    }

    private clearAEndData() {
        this.model.aEndSlots = [];
        this.model.aEndPorts = [];
        this.aEndPortDisabled = true;
        this.aEndMuxDisabled = true;
        this.model.aEndHasMux = false;
        this.model.aEndMux = null;
        this.aEndMuxOptions = [];
        this.showAEndLabelMuxEmpty = false;
    }

    private clearZEndData() {
        this.model.zEndSlots = [];
        this.model.zEndPorts = [];
        this.zEndPortDisabled = true;
        this.zEndMuxDisabled = true;
        this.model.zEndHasMux = false;
        this.model.zEndMux = null;
        this.zEndMuxOptions = [];
        this.showZEndLabelMuxEmpty = false;
    }

    hasChanges(): boolean {
        return this.lastChangeToBeApplied != null;
    }

    getDependenciesStepsNames(): string[] {
        return [
            "TranspondersStepModel",
            "IntermediatesStepModel",
            "LinksStepModel",
            "AmplifiersStepModel",
            "OscManagementStepModel"
        ];
    }

    private async addDevice(values: any) {
        const endDevice = values[0];
        const inputEndDevice: NmsInputButtonComponent = values[1];
        const device: Device = values[2];

        await this.applyDevicesAfterSelection(endDevice, inputEndDevice, device);
    }

    private changeEndSlot(values: any) {
        const endSlots = values[0];
        const endPortOptions = values[1];
        const newValue = values[2];

        this.model[endSlots] = newValue;
        this.pendingModel[endSlots] = [];

        const allPorts: PortData[] = [];

        if (this.model[endSlots].length) {
            this.model[endSlots].filter(slot => Array.isArray(slot.ports))
                .forEach(slot => allPorts.push(...slot.ports));
        }

        this[endPortOptions] = allPorts;

        const isDisabled = this.model[endSlots]?.length === 0;

        if (endSlots === "aEndSlots") {
            this.setAEndPorts(isDisabled);
            this.showAEndLabelMuxEmpty = this.model.aEndHasMux && this.model[endSlots]?.length === 0;
        } else {
            this.setZEndPorts(isDisabled);
            this.showZEndLabelMuxEmpty = this.model.zEndHasMux && this.model[endSlots]?.length === 0;
        }
    }

    private changeEndPort(values: any) {
        const endPorts = values[0];
        const ports = values[1];

        this.model[endPorts] = ports;
        this.pendingModel[endPorts] = [];
    }

    private changeEndMuxChange(values: any) {
        const endMux = values[0];
        const mux = values[1];

        this.model[endMux] = mux;
        this.pendingModel[endMux] = null;
    }

    private changeAEndMuxCheck(values: any) {
        const hasMux = values[0];

        this.model.aEndHasMux = hasMux;
        this.pendingModel.aEndHasMux = null;

        if (hasMux) {
            this.loadMuxes(this.model.aEndDevice.id)
                .then(muxes => this.updateAEndMuxValues(muxes, values[0]));
        } else {
            this.updateAEndMuxValues([]);
        }
    }

    private changeZEndMuxCheck(values: any) {
        const hasMux = values[0];

        this.model.zEndHasMux = hasMux;
        this.pendingModel.zEndHasMux = null;

        if (hasMux) {
            this.loadMuxes(this.model.zEndDevice.id)
                .then(muxes => this.updateZEndMuxValues(muxes, values[0]));
        } else {
            this.updateZEndMuxValues([]);
        }
    }
}
